import React, { lazy, Suspense } from "react"

import { Redirect, Switch, Route } from "react-router-dom"

import { SIDEBAR_MENU } from "@constants/sidebar"
import { SLACK_INTEGRATION_ENABLED } from "@constants/global"

import { ChangePasswordModal } from "./components/ChangePasswordModal"
import {
  ArchivePage,
  Auth0ContentPage,
  ComingUpPage,
  InboxPage,
  NewSignInPage,
  NewSignUpPage,
  TeeupPreviewPage,
  TrashPage,
} from "./pages"
import ChangeTempPassword from "./pages/ChangeTempPassword"
import ContactMethods from "./pages/ContactMethods"
import { ForgotPassword } from "./pages/ForgotPassword"
import MessagesPage from "./pages/MessagesPage"
import SignUp from "./pages/SignUpPage"
import PrivateRoute from "./routes/PrivateRoute"
import PublicRoute from "./routes/PublicRoute"

const VerifyPage = lazy(() => import("./pages/VerifyPage"))
const SlackLogin = lazy(() => import("./pages/SlackLogin"))
const SettingsPage = lazy(() => import("./pages/SettingsPage"))
const CalendarPage = lazy(() => import("./pages/CalendarPage"))
const ChangePassword = lazy(() => import("./pages/ChangePassword"))
const VerifyInvitation = lazy(() => import("./pages/VerifyInvitation"))
const SignInEmail = lazy(() => import("./pages/SignInPage/SignInEmail"))
const SignInSocial = lazy(() => import("./pages/SignInPage/SingleSignInPage"))
const MyProfileNd = lazy(() => import("./pages/MyProfileNd/MyProfileNd"))
const Profile = lazy(() => import("./pages/Profile"))
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"))
const ContactsPageNd = lazy(() => import("./pages/ContactPageNd/ContactPageNd"))
const UserProfilePage = lazy(() => import("./pages/ProfilePage"))
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"))
const TeeUpPage = lazy(() => import("./pages/TeeUpPage"))
// wave4-nav-ia: shared "to discuss" template page (live doors for unbuilt nav destinations).
const DiscussPage = lazy(() => import("./pages/DiscussPage"))
// wave4-nav-ia fix6: PlanFlow page (single rail entry; selector + internal nav live inside)
const PlanFlowPage = lazy(() => import("./pages/PlanFlowPage"))
// wave4-nav-ia fix7: People page (single rail entry; Contacts|SocialDesktop pills inside)
const PeoplePage = lazy(() => import("./pages/PeoplePage"))
// wave4-fix12: in-app OSS licenses page (licenses.coowe.com is dead)
const LicensesPage = lazy(() => import("./pages/LicensesPage"))
// wave4-fix22: in-app Privacy Policy + Terms of Service (privacy/tos.coowe.com are dead;
// content = the legacy legal docs, knowingly out of date per QJ ruling)
const PrivacyPage = lazy(() =>
  import("./pages/LegalPage").then(m => ({ default: m.PrivacyPage }))
)
const TermsPage = lazy(() =>
  import("./pages/LegalPage").then(m => ({ default: m.TermsPage }))
)
// CREATE-AI-V1 2026-07-03: prompt-first create flow (dev-backend only entry, see
// SideBar/CreateTeeupButton.js). Classic CreateTeeUpNd modal flow is untouched.
const CreateAiPage = lazy(() => import("./pages/CreateAiPage"))

const useRoutes = () => {
  return (
    <Suspense fallback={<span>loading...</span>}>
      <Switch>
        {SLACK_INTEGRATION_ENABLED && (
          <PrivateRoute path="/slack-integration" component={SlackLogin} exact />
        )}
        <PublicRoute
          isDisabledRedirect
          isSharedLink
          path="/s/:slug"
          component={TeeupPreviewPage}
          exact
        />
        <PublicRoute
          isDisabledRedirect
          path="/t/:slug"
          component={TeeupPreviewPage}
          exact
        />
        <PublicRoute
          path="/new-sign-in-email"
          component={NewSignInPage}
          exact
        />
        <PublicRoute path="/sign-in-with" component={Auth0ContentPage} exact />
        <PublicRoute path="/sign-up-with" component={Auth0ContentPage} exact />
        <PublicRoute path="/sign-up-email" component={NewSignUpPage} />
        <PublicRoute path="/sign-in" component={SignInSocial} exact />
        <PublicRoute path="/sign-in-email" component={SignInEmail} exact />
        <PublicRoute path="/sign-up" component={SignUp} exact />
        <PublicRoute path="/forgot-password" component={ForgotPassword} exact />
        <PublicRoute
          path="/forgot-password/confirm/:token"
          component={ForgotPassword}
          exact
        />
        <PublicRoute
          path="/verify-invitation"
          component={VerifyInvitation}
          exact
        />
        <Route path="/users/email/delete/:token" component={InboxPage} exact />
        <Route
          path="/users/email/delete/cancel/:token"
          component={InboxPage}
          exact
        />

        <PrivateRoute
          path={SIDEBAR_MENU.INBOX.PATHNAME}
          component={InboxPage}
          exact
        />
        <PrivateRoute
          path={SIDEBAR_MENU.ARCHIVE.PATHNAME}
          component={ArchivePage}
          exact
        />
        <PrivateRoute
          path={SIDEBAR_MENU.TRASH.PATHNAME}
          component={TrashPage}
          exact
        />
        <PrivateRoute
          path={SIDEBAR_MENU.COMING_UP.PATHNAME}
          component={ComingUpPage}
          exact
        />
        {/* new path for profile */}
        <PrivateRoute
          path={SIDEBAR_MENU.MY_PROFILE.PATHNAME}
          component={MyProfileNd}
          exact
        />
        <PrivateRoute
          path={SIDEBAR_MENU.MY_PROFILE_2.PATHNAME}
          component={Profile}
          exact
        />

        <PrivateRoute path="/messages" component={MessagesPage} exact />
        <PrivateRoute
          path="/notifications"
          component={NotificationsPage}
          exact
        />
        <PrivateRoute
          path={SIDEBAR_MENU.CALENDAR.PATHNAME}
          component={CalendarPage}
          exact
        />
        {/*temp path for contacts page*/}
        <PrivateRoute
          path={SIDEBAR_MENU.CONTACTS.PATHNAME}
          component={ContactsPageNd}
          exact
        />
        <PrivateRoute
          path={SIDEBAR_MENU.SETTINGS.PATHNAME}
          component={SettingsPage}
          exact
        />
        <PrivateRoute
          path="/change-password"
          component={ChangePassword}
          exact
        />
        <PublicRoute path="/" exact>
          <Redirect to="/sign-in" />
        </PublicRoute>
        <PrivateRoute path="/contact" component={ContactMethods} exact />
        {/* wave4-nav-ia: to-discuss pages (Goals/Tasks/Booking/Prioritize/Messages/Enterprise) */}
        <PrivateRoute path="/planflow" component={PlanFlowPage} exact />
        <PrivateRoute path="/people" component={PeoplePage} exact />
        <PrivateRoute path="/licenses" component={LicensesPage} exact />
        <PrivateRoute path="/privacy" component={PrivacyPage} exact />
        <PrivateRoute path="/terms" component={TermsPage} exact />
        <PrivateRoute path="/discuss/:feature" component={DiscussPage} exact />
        {/* wave4-nav-ia: alias so /relationships (the new People naming) lands on the same
            Contacts view; /contacts itself is unchanged so no old URL breaks. */}
        <PrivateRoute
          path="/relationships"
          component={() => <Redirect to="/contacts" />}
          exact
        />
        <PrivateRoute path="/teeUps/:id" component={TeeUpPage} exact />
        <PrivateRoute path="/create-ai" component={CreateAiPage} exact />
        <PrivateRoute path="/profile/:id" component={UserProfilePage} exact />
        <PrivateRoute
          path="/users/email/resetpassword/:token"
          component={ChangePasswordModal}
          exact
        />

        <Route
          path="/verify/:token"
          render={props => <VerifyPage {...props} />}
        />
        <Route
          path="/change-temp-password"
          render={props => <ChangeTempPassword {...props} />}
        />

        <Route path="/not-found" component={NotFoundPage} />
        <Route path="*" component={NotFoundPage} />
      </Switch>
    </Suspense>
  )
}

export default useRoutes
